// Step 7.1 commit 2 / AC246: cutoffs.ts is now a thin compatibility
// wrapper over lib/delivery/next-deliveries. The previous Step-7
// implementation iterated UTC weekdays which broke after ~8pm ET; the
// ET-aware module is the single source of truth.

import {
  getNextDeliveries as canonicalNext,
  getNextDelivery as canonicalNextDelivery,
  cutoffForIso,
  isoFromEtDay,
  etDayFromIso,
} from "./next-deliveries";

export type DeliveryDayOfWeek = 1 | 3;

export type AvailableDeliveryDate = {
  date: Date;
  cutoff: Date;
  dayOfWeek: DeliveryDayOfWeek;
};

export function cutoffForDelivery(delivery: Date): Date {
  return cutoffForIso(isoOf(delivery));
}

function isoOf(d: Date): string {
  // Use the ET calendar day of the input. Anchored at noon UTC, the
  // anchor's UTC date IS the ET date by construction.
  return isoFromEtDay({
    year: d.getUTCFullYear(),
    month: d.getUTCMonth(),
    day: d.getUTCDate(),
  });
}

export function nextAvailableDates(
  now: Date,
  options: {
    count?: number;
    isBlocked?: (date: Date) => boolean;
  } = {},
): AvailableDeliveryDate[] {
  const { count = 3, isBlocked } = options;
  const wrappedBlocked = isBlocked
    ? (iso: string) => {
        const et = etDayFromIso(iso);
        return isBlocked(new Date(Date.UTC(et.year, et.month, et.day, 12)));
      }
    : undefined;
  const list = canonicalNext(now, { count, isBlocked: wrappedBlocked });
  return list.map((opt) => ({
    date: opt.date,
    cutoff: opt.cutoff,
    dayOfWeek: opt.weekday,
  }));
}

export function nextCutoffFrom(
  now: Date,
  options: { isBlocked?: (date: Date) => boolean } = {},
): { cutoff: Date; delivery: Date } | null {
  const wrappedBlocked = options.isBlocked
    ? (iso: string) => {
        const et = etDayFromIso(iso);
        return options.isBlocked!(
          new Date(Date.UTC(et.year, et.month, et.day, 12)),
        );
      }
    : undefined;
  const next = canonicalNextDelivery(now, { isBlocked: wrappedBlocked });
  if (!next) return null;
  return { cutoff: next.cutoff, delivery: next.date };
}

export function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function parseIsoDate(iso: string): Date {
  const et = etDayFromIso(iso);
  // Use noon UTC anchor to avoid timezone drift when consumers format.
  return new Date(Date.UTC(et.year, et.month, et.day, 12, 0, 0, 0));
}
